import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import { Transition, TransitionGroup } from 'react-transition-group';
import { AnimationDuration } from '../utils/Animation';

export interface HeightAnimatorProps {
  /** Callback function to be called when the close animation has completed */
  onExitAnimationComplete?: () => void;
  /**
   * By default this widget triggers animations based on the addition/removal of its children
   * Using the height prop, will instead trigger animation based on this value changing
   * This prop is to be used when you would like to animate an element without removing it from the DOM
   */
  height?: 0 | 'auto';
  /** Sets an initial height, which either allows/prevents an initial animation */
  initialHeight?: 0 | 'auto';
}

export const HeightAnimator: React.FC<HeightAnimatorProps> = props => {
  const [transitionHeight, setTransitionHeight] = React.useState<0 | 'auto'>(props.initialHeight || 0);
  const handleEnter = (): void => {
    if (props.height === undefined) {
      setTransitionHeight('auto');
    }
  };

  const handleExit = (): void => {
    setTransitionHeight(0);
  };

  const childrenArray = React.Children.toArray(props.children);
  const firstChildArray = childrenArray.slice(0, 1);

  if (childrenArray.length > 1) {
    console.warn('Multiple children were provided, but only the first child will be rendered');
  }

  React.useEffect(() => {
    if (props.height !== undefined && props.height !== transitionHeight) {
      setTransitionHeight(props.height);
    }
  }, [props.height, transitionHeight]);

  return (
    <TransitionGroup
      // TransitionGroup will not update exiting children's props without a childFactory
      childFactory={child => React.cloneElement(child, {
        height: transitionHeight,
      })}
    >
      {firstChildArray.map((child, id) =>
        <Transition
          appear
          key={id}
          onEnter={handleEnter}
          onExit={handleExit}
          onExited={props.onExitAnimationComplete}
          timeout={AnimationDuration}
        >
          <AnimateHeight
            animateOpacity={true}
            duration={AnimationDuration}
            height={transitionHeight}
          >
            {child}
          </AnimateHeight>
        </Transition>)}
    </TransitionGroup>
  );
};
